import { ReactNode, useEffect, type JSX } from "react";
import { FormattedMessage } from "react-intl";
import { Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import FormDialog, { ButtonAction } from "../../FormDialog/FormDialog";
import FlyInDrawer from "../../FlyInDrawer/FlyInDrawer";
import FlyInDrawerHeader from "../../Common/FlyInDrawerHeader/FlyInDrawerHeader";
import FlyInDrawerFooter from "../../Common/FlyInDrawerFooter/FlyInDrawerFooter";
import ManualAddressFields, { IManualAddressFormValues, defaultManualAddressValues } from "./ManualAddressFields";

/**
 * NG-7511: manual address-entry fallback shown when Google Places is unavailable (or as the
 * always-on "Edit address" affordance). Renders as a fly-in by default; pass `variant="modal"`
 * when the host is itself an overlay (a modal/Dialog or a fly-in) — a fly-in cannot render usably
 * on top of another overlay.
 */
export type TManualAddressOverlayVariant = "flyin" | "modal";

interface IProps {
    open: boolean;
    variant?: TManualAddressOverlayVariant;
    title?: ReactNode;
    initialValues?: Partial<IManualAddressFormValues>;
    onSave: (values: IManualAddressFormValues) => void;
    onClose: () => void;
}

const defaultTitle = <FormattedMessage id="manual-address.title.overlay" defaultMessage="Enter address manually" />;

const ManualAddressOverlay = ({ open, variant = "flyin", title, initialValues, onSave, onClose }: IProps): JSX.Element => {
    const { control, handleSubmit, reset } = useForm<IManualAddressFormValues>({
        defaultValues: { ...defaultManualAddressValues, ...initialValues },
    });

    // Re-seed the form whenever the overlay (re)opens so it reflects the latest composed address.
    useEffect(() => {
        if (open) {
            reset({ ...defaultManualAddressValues, ...initialValues });
        }
    }, [open]);

    const onValid = (values: IManualAddressFormValues): void => {
        onSave(values);
        onClose();
    };

    const submit = handleSubmit(onValid);
    const resolvedTitle = title ?? defaultTitle;
    const body = (
        <>
            <Typography variant="body2" sx={{ marginTop: "12px", marginBottom: "4px", color: "text.secondary" }}>
                <FormattedMessage
                    id="manual-address.subtitle.overlay"
                    defaultMessage="Address lookup is currently unavailable. Please enter the address details below."
                />
            </Typography>
            <ManualAddressFields control={control} />
        </>
    );

    if (variant === "modal") {
        return (
            <FormDialog
                id="manual-address-overlay"
                open={open}
                showTitle
                title={resolvedTitle}
                showCancelBtn
                fullWidth
                maxWidth="sm"
                onClose={(action) => {
                    if (action === ButtonAction.SAVE) {
                        submit();
                        return;
                    }
                    onClose();
                }}
            >
                {body}
            </FormDialog>
        );
    }

    return (
        <FlyInDrawer isOpen={open} onClose={onClose} closeDrawer={onClose}>
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <FlyInDrawerHeader close={onClose} title={resolvedTitle as JSX.Element} />
                <Box sx={{ flex: 1, overflowY: "auto", padding: "0 24px" }}>{body}</Box>
                <FlyInDrawerFooter
                    onCloseBtn={onClose}
                    onSubmitBtn={submit}
                    closeBtnTxt={{ id: "common.button-label.cancel", defaultMessage: "Cancel" }}
                    submitBtnTxt={{ id: "manual-address.overlay.label.save", defaultMessage: "Save address" }}
                />
            </Box>
        </FlyInDrawer>
    );
};

export default ManualAddressOverlay;
